import {
  Expand,
  Focus,
  Link2,
  Minimize2,
  PanelLeftClose,
  Repeat2,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import type { BridgeRatio, PortfolioBookKindV2 } from "@tradvera/contracts";
import { AppShell, Bilingual, StatusPill, usePreview } from "@tradvera/design-system";
import { AccountControl, useAuth } from "./Auth";
import { TradveraLanding } from "./TradveraLanding";

type Screen = "home" | "academy" | "trade" | "bridge";
type TradeEntryView = "command" | "research" | "company-evaluation" | "opportunity" | "my-portfolio" | "production" | "technical" | "decision";
const academyCoachEndpoint = import.meta.env.VITE_ACADEMY_COACH_ENDPOINT ?? "https://api.tradvera.com/academy/coach";
const tradeEvidenceEndpoint = import.meta.env.VITE_TRADE_EVIDENCE_ENDPOINT ?? "https://api.tradvera.com/v1/trade/evidence";
const AcademyWorkspace = lazy(() => import("@tradvera/academy-domain").then((module) => ({ default: module.AcademyWorkspace })));
const TradeWorkspace = lazy(() => import("@tradvera/trade-domain").then((module) => ({ default: module.TradeWorkspace })));

export function PortalApp() {
  const [initialRoute] = useState(readInitialPortalRoute);
  const [screen, setScreen] = useState<Screen>(initialRoute.screen);
  const [tradeInitialView, setTradeInitialView] = useState<TradeEntryView>(initialRoute.tradeView);
  const [portfolioInitialKind, setPortfolioInitialKind] = useState<PortfolioBookKindV2 | null>(initialRoute.portfolioKind);
  const { session } = useAuth();
  const navigate = useCallback((next: Screen) => {
    if (next === "trade") { setTradeInitialView("command"); setPortfolioInitialKind(null); }
    setScreen(next);
  }, []);
  const openTrade = useCallback((nextView: TradeEntryView) => {
    setTradeInitialView(nextView);
    setPortfolioInitialKind(null);
    setScreen("trade");
  }, []);
  useEffect(() => {
    if (!initialRoute.deepLink || typeof window === "undefined") return;
    const clean = new URL(window.location.href);
    clean.searchParams.delete("screen"); clean.searchParams.delete("view"); clean.searchParams.delete("portfolio");
    window.history.replaceState(window.history.state, "", `${clean.pathname}${clean.search}${clean.hash}`);
  }, [initialRoute]);
  useEffect(() => {
    const openSearch = () => {
      setTradeInitialView("research");
      setScreen("trade");
    };
    window.addEventListener("tradvera:open-search", openSearch);
    return () => window.removeEventListener("tradvera:open-search", openSearch);
  }, []);
  return (
    <AppShell surface="portal" active={screen} onNavigate={(id) => navigate(id as Screen)} guideSurface={screen === "academy" || screen === "trade" ? screen : undefined} title={screen === "home" ? "الرئيسية" : screen === "academy" ? "Tradvera Academy" : screen === "trade" ? "Tradvera Trade" : "Bridge Mode"} eyebrow={session ? "TRADVERA ID" : "FOUNDER PREVIEW"} account={<AccountControl />}>
      {screen === "home" && <TradveraLanding onNavigate={navigate} onOpenTrade={openTrade} />}
      <Suspense fallback={<WorkspaceLoading />}>
        {screen === "academy" && <AcademyWorkspace coachEndpoint={academyCoachEndpoint} account={session} />}
        {screen === "trade" && <TradeWorkspace initialView={tradeInitialView} portfolioInitialKind={portfolioInitialKind}
          account={session} evidenceEndpoint={tradeEvidenceEndpoint} externalControls />}
        {screen === "bridge" && <BridgeWorkspace onExit={() => setScreen("home")} />}
      </Suspense>
    </AppShell>
  );
}

export function initialPortalRouteFromUrl(value: string): {
  screen: Screen; tradeView: TradeEntryView; portfolioKind: PortfolioBookKindV2 | null; deepLink: boolean;
} {
  const url = new URL(value, "https://tradvera.com");
  const requestedScreen = url.searchParams.get("screen");
  const requestedView = url.searchParams.get("view");
  const tradeViews: TradeEntryView[] = ["command", "research", "company-evaluation", "opportunity", "my-portfolio", "production", "technical", "decision"];
  const tradeView = tradeViews.includes(requestedView as TradeEntryView) ? requestedView as TradeEntryView : "command";
  const requestedPortfolio = url.searchParams.get("portfolio");
  const portfolioKind = requestedPortfolio === "paper" ? "PAPER"
    : requestedPortfolio === "owned" ? "OWNED_PRIVATE" : null;
  const directTrade = requestedScreen === "trade" || requestedView !== null;
  return { screen: directTrade ? "trade" : "home", tradeView, portfolioKind,
    deepLink: directTrade || requestedPortfolio !== null };
}

function readInitialPortalRoute() {
  return initialPortalRouteFromUrl(typeof window === "undefined" ? "https://tradvera.com/" : window.location.href);
}

function WorkspaceLoading() {
  return <div className="workspace-loading" role="status" aria-live="polite"><Sparkles size={21}/><span>بنجهّز مساحة العمل…</span><small>LOADING VERIFIED MODULE</small></div>;
}

function BridgeWorkspace({ onExit }: { onExit: () => void }) {
  const { preferences, patchPreferences } = usePreview();
  const [swapped, setSwapped] = useState(false);
  const [focused, setFocused] = useState<"academy" | "trade" | null>(null);
  const ratios: BridgeRatio[] = ["50/50", "65/35", "75/25"];
  const [large, small] = preferences.ratio.split("/").map(Number);
  const academyShare = preferences.ratio === "50/50" ? 50 : swapped ? small : large;
  const tradeShare = 100 - academyShare;
  return (
    <div className="bridge-workspace">
      <header className="bridge-header"><div><span className="workspace-code">CONTROLLED CONTEXT LINK</span><h1>تعلّم وتابع — من دون خلط الصلاحيات.</h1><p>Bridge Mode يفتح نافذتين، لكنه لا يصنع قرارًا ماليًا من محتوى تعليمي.</p></div><StatusPill tone="teal"><ShieldCheck size={13}/> SAFE CONTEXT</StatusPill></header>
      <div className="bridge-controls"><div className="ratio-control"><span>تقسيم الشاشة</span>{ratios.map((ratio)=><button key={ratio} className={preferences.ratio===ratio?"is-active":""} onClick={()=>patchPreferences({ratio})}>{ratio}</button>)}</div><div className="bridge-tools"><button onClick={()=>setSwapped(!swapped)}><Repeat2 size={15}/> تبديل</button><button onClick={()=>setFocused(focused?null:"academy")}><Focus size={15}/> تركيز</button><button onClick={onExit}><PanelLeftClose size={15}/> إغلاق</button></div></div>
      <div className={`bridge-stage ${focused ? `focus-${focused}` : ""}`} style={{"--academy-share":`${academyShare}fr`,"--trade-share":`${tradeShare}fr`} as React.CSSProperties}>
        <section className="bridge-pane academy-pane"><div className="pane-label"><span>ACADEMY</span><small>{academyShare}%</small><button onClick={()=>setFocused(focused==="academy"?null:"academy")} aria-label="تكبير الأكاديمية">{focused==="academy"?<Minimize2 size={14}/>:<Expand size={14}/>}</button></div><AcademyWorkspace compact/></section>
        <div className="bridge-seam"><span><Link2 size={14}/></span><i/><small>CONTEXT ONLY</small><i/></div>
        <section className="bridge-pane trade-pane"><div className="pane-label"><span>TRADE</span><small>{tradeShare}%</small><button onClick={()=>setFocused(focused==="trade"?null:"trade")} aria-label="تكبير التداول">{focused==="trade"?<Minimize2 size={14}/>:<Expand size={14}/>}</button></div><TradeWorkspace compact/></section>
      </div>
      <div className="bridge-contract"><ShieldCheck size={16}/><Bilingual ar="تم نقل: موضوع الدرس + هدف التعلم + معرّفات الأدلة فقط" en="Transferred: lesson subject + objective + evidence IDs only"/><span>لا ملكية · NO HOLDINGS</span><span>لا أوامر · NO ORDERS</span></div>
    </div>
  );
}
